import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { useVirtualizer } from '@tanstack/react-virtual';
import { type Colour, usePermissions, usePreference } from '@tyro/api';
import {
  Scrollbar,
  getBaseColorBasedOnString,
  useContrastClass,
  useDisclosure,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { ChevronRightIcon, NewIcon } from '@tyro/icons';
import { Link } from '@tyro/router/components';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import type { WhatsNewListData } from '../../../@types/whats-new';
import { useWhatsNewList } from '../../../api/whats-new';
import { IconButtonAnimate } from '../../icon-button-animate';

export function WhatsNewPopover() {
  const { t } = useTranslation(['common']);
  const { palette } = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { userType, hasAtLeastOnePermission } = usePermissions();
  const [lastViewedDateTime, setLastViewedDateTime] = usePreference(
    'whats-new.last-viewed-date-time',
    '',
  );

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useWhatsNewList(userType);

  const filteredListItems = useMemo(
    () =>
      data?.pages.filter(
        ({ filter_permissions }) =>
          !filter_permissions ||
          filter_permissions.length === 0 ||
          hasAtLeastOnePermission(filter_permissions),
      ) ?? [],
    [data, hasAtLeastOnePermission],
  );

  const virtualizer = useVirtualizer({
    count: filteredListItems.length ?? 0,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 317,
    overscan: 10,
    paddingStart: 2,
    paddingEnd: 2,
  });

  const unreadCount = useMemo(() => {
    if (lastViewedDateTime === '') {
      return filteredListItems.length;
    }
    return filteredListItems.filter(({ published_date }) =>
      dayjs(published_date).isAfter(lastViewedDateTime),
    ).length;
  }, [filteredListItems, lastViewedDateTime]);

  const virtualList = virtualizer.getVirtualItems();
  const lastVirtualItemIndex = virtualList[virtualList.length - 1]?.index;

  useEffect(() => {
    if (
      lastVirtualItemIndex === filteredListItems.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    lastVirtualItemIndex,
    filteredListItems.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const handleOpen = () => {
    onOpen();
    setLastViewedDateTime(new Date().toISOString());
  };

  return (
    <>
      {filteredListItems.length > 0 && (
        <IconButtonAnimate
          color={isOpen ? 'primary' : 'default'}
          onClick={handleOpen}
          sx={{ width: 40, height: 40 }}
        >
          <Badge
            badgeContent={unreadCount}
            max={10}
            color="primary"
            sx={{
              '& .MuiBadge-badge': {
                minWidth: 16,
                height: 16,
                padding: '0 4px',
              },
            }}
            overlap="circular"
          >
            <NewIcon />
          </Badge>
        </IconButtonAnimate>
      )}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        slotProps={{
          transition: { onEntering: () => virtualizer.measure() },
          root: {
            sx: {
              '& .MuiBackdrop-root': {
                opacity: '0 !important',
              },
            },
          },
          paper: {
            sx: ({ palette }) => ({
              width: '100%',
              maxWidth: 320,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px 0 0 24px',
              border: '1px solid',
              borderRight: 'none',
              borderColor: 'indigo.50',
              boxShadow: `0px 4px 74px ${alpha(
                palette.indigo[300],
                0.3,
              )} !important`,
            }),
          },
        }}
      >
        <Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 1,
              pl: 2,
              borderBottom: 'solid 1px',
              borderBottomColor: 'divider',
              backgroundColor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
          >
            <Typography component="h2" variant="h6">
              {t('common:whatsNewInTyro')}
            </Typography>
          </Stack>

          <Scrollbar
            ref={containerRef}
            style={{ height: 'calc(100vh - 46px)' }}
            containerProps={{
              sx: {
                borderRadius: '24px 0 0 24px',
              },
            }}
          >
            <List
              disablePadding
              sx={{
                backgroundColor: 'transparent',
                '& .MuiListSubheader-root': {
                  color: 'text.primary',
                  fontWeight: 'bold',
                  backgroundColor: 'transparent',
                  py: 1,
                  lineHeight: 1.5,
                },
                '& .MuiListItemButton-root': {
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  px: 1,
                  mx: 1,
                  my: 0.75,
                  borderRadius: 2.5,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                },
                position: 'relative',
                height: virtualizer.getTotalSize(),
              }}
            >
              {virtualList.map((virtualRow) => {
                const filteredListItem = filteredListItems[virtualRow.index];
                const itemColor = getBaseColorBasedOnString(
                  filteredListItem.title,
                );

                return (
                  <ListItem
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    disablePadding
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <ListItemButton
                      component={Link}
                      to={`/whats-new/view/${filteredListItem.id}`}
                      sx={{
                        backgroundColor: alpha(palette[itemColor].main, 0.02),
                        borderColor: `${itemColor}.200`,
                        '&:hover': {
                          bgcolor: alpha(palette[itemColor].main, 0.1),
                        },
                      }}
                      onClick={onClose}
                    >
                      <WhatsNewCardHeader
                        item={filteredListItem}
                        itemColor={itemColor}
                      />
                      <ListItemText
                        sx={{
                          mx: 1,
                          mt: 1.5,
                          mb: 1,
                          '& .MuiTypography-body1': {
                            color: `${itemColor}.700`,
                            fontWeight: 600,
                          },
                        }}
                        primary={filteredListItem.title}
                        secondary={
                          <>
                            <span>{filteredListItem.summary}</span>
                            <ChevronRightIcon sx={{ mb: '-7px', ml: 0.25 }} />
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Scrollbar>
        </Stack>
      </Drawer>
    </>
  );
}

function WhatsNewCardHeader({
  item,
  itemColor,
}: { item: WhatsNewListData; itemColor: Colour }) {
  const { palette } = useTheme();
  const noticeColorPalette = palette[itemColor];
  const { published_date } = item;
  const date = dayjs(published_date).format('L');

  const backgroundImageUrl = `https://cms.tyro.school/assets/${item.preview_image}?width=285&height=220`;

  const noticeContrastClass = useContrastClass(
    noticeColorPalette[200],
    backgroundImageUrl,
  );

  const gradient = `linear-gradient(90deg, ${noticeColorPalette[100]} 0%, ${noticeColorPalette[300]} 100%)`;

  return (
    <Box
      className={`header ${noticeContrastClass}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        background: backgroundImageUrl
          ? `url(${backgroundImageUrl}), ${gradient}`
          : gradient,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        width: '100%',
        borderRadius: 1.75,
        minHeight: 220,
        border: '1px solid',
        borderColor: `${itemColor}.100`,

        '& .date': {
          backdropFilter: 'blur(16px)',
        },

        '&.light': {
          '& .date': {
            backgroundColor: alpha(palette.common.white, 0.7),
            color: 'text.primary',
          },
        },
        '&.dark': {
          '& .date': {
            backgroundColor: alpha(palette.common.black, 0.7),
            color: 'white',
          },
        },
      }}
    >
      <Box
        sx={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: 'row-reverse',
          px: 0.75,
        }}
      >
        <Chip className="date" size="small" label={date} />
        {/* Hiding to figure out what tags make sense for users */}
        {/* <NoticeTags
          assignedToParties={notice.assignedToParties}
          color={noticeColor}
        /> */}
      </Box>
    </Box>
  );
}
